import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Linkedin, Twitter, Instagram } from 'lucide-react';
import { staff } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

const StaffSection = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      const cards = cardsRef.current.children;
      gsap.fromTo(
        cards,
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: cardsRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Separate directors from other staff
  const directors = staff.filter(m => m.role.includes('Director'));
  const otherStaff = staff.filter(m => !m.role.includes('Director'));

  return (
    <section
      id="staff"
      ref={sectionRef}
      className="relative py-16 lg:py-28 bg-[#0a0a0a] overflow-hidden"
      data-testid="staff-section"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-10 lg:mb-16">
          <span className="inline-block text-white/50 text-xs font-light tracking-[0.3em] uppercase mb-6">
            Nuestro Equipo
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
            Staff{' '}
            <span className="text-white/50 font-medium">FARO Sports</span>
          </h2>
          <p className="text-white/60 text-base font-light max-w-2xl mx-auto">
            Ex futbolistas profesionales con dilatadas trayectorias en equipos de primer nivel en Uruguay y el mundo
          </p>
        </div>

        {/* Staff Cards - Grid with circular photos */}
        <div ref={cardsRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Directors first, then other staff */}
          {[...directors, ...otherStaff].map((member) => (
            <div
              key={member.id}
              className="group flex flex-col items-center"
              data-testid={`staff-card-${member.id}`}
            >
              {/* Circular Photo */}
              <div className="relative w-28 h-28 sm:w-32 sm:h-32 lg:w-36 lg:h-36 mb-5 flex-shrink-0">
                <div className="w-full h-full rounded-full overflow-hidden border-2 border-white/10 group-hover:border-white/30 transition-all duration-500">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
                      <span className="text-3xl font-light text-white/20">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Social Links - Appear on hover */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  {member.twitter && member.twitter !== '#' && (
                    <a
                      href={member.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-black/80 backdrop-blur-sm hover:bg-white rounded-full flex items-center justify-center text-white hover:text-black transition-all duration-300 border border-white/20"
                    >
                      <Twitter size={14} />
                    </a>
                  )}
                  {member.instagram && member.instagram !== '#' && (
                    <a
                      href={member.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-black/80 backdrop-blur-sm hover:bg-white rounded-full flex items-center justify-center text-white hover:text-black transition-all duration-300 border border-white/20"
                    >
                      <Instagram size={14} />
                    </a>
                  )}
                  {member.linkedin && member.linkedin !== '#' && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-black/80 backdrop-blur-sm hover:bg-white rounded-full flex items-center justify-center text-white hover:text-black transition-all duration-300 border border-white/20"
                    >
                      <Linkedin size={14} />
                    </a>
                  )}
                </div>
              </div>

              {/* Info - Fixed width containers for alignment */}
              <div className="flex flex-col items-center text-center w-full max-w-[180px]">
                {/* Role badge - same size for all */}
                <div className="w-full h-14 flex items-center justify-center mb-3">
                  <span className="w-full px-2 py-2 bg-white/10 text-white/60 text-[9px] font-medium rounded-lg uppercase tracking-wider text-center leading-tight flex items-center justify-center">
                    {member.role}
                  </span>
                </div>
                {/* Name */}
                <h3 className="text-sm sm:text-base font-medium text-white mb-2 leading-tight">
                  {member.name}
                </h3>
                {/* Bio - more lines visible */}
                <p className="text-white/40 text-[11px] font-light leading-relaxed hidden sm:block">
                  {member.bio.split('.').slice(0, 2).join('.')}.
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StaffSection;
